/**
 * Ideas of a workspace: cards of writing (a logline, a scene, a thought, a task) on the
 * workspace's Ideas page, put wherever people drag them. Every card has a name to know it by.
 * A card is kept (locked) when someone likes it, reacted to with emoji by the people of the
 * workspace, and given to one of them as a task and ticked when done. A Think up card asks a
 * model for cards, which land beside it with what they cost. The people of a card are the
 * people of its workspace: its owner and everyone it is shared with.
 *
 * Pure functions on plain data; utils/ideaStore.ts keeps ideas in the cloud and tells people.
 */

export interface WorkspacePerson {
    id: string;
    name: string;
    email: string;
    picture?: string;
    permission: 'owner' | 'edit' | 'view';
}

import type { Paper, ScreenplayMeta } from './screenplay';
import type { PromptFor, PromptTarget } from './promptWriter';

/** Where a card came from: written by hand, made by a model, sent from a node on the canvas */
export type IdeaSource = 'written' | 'ai' | 'node';

/** What a Think up card asks a model: whatever is written in it, and whether the kept cards go along */
export interface IdeaAsk {
    topic: string;
    buildOnKept: boolean;
    /** What the answer is: a card of writing (the default), a screenplay card, so many pages long on this paper, or a prompt for a node (utils/promptWriter.ts) */
    format?: 'card' | 'screenplay' | 'prompt';
    pages?: number;
    paper?: Paper;
    /** A prompt: for which node, written for which generator, at what length (video) and in what frame */
    target?: PromptTarget;
    generator?: string;
    length?: string;
    frame?: string;
}

/** One time a Think up card was asked: what it cost and what came of it */
export interface AskRun {
    at: number;
    model: string;
    inputTokens: number | null;
    outputTokens: number | null;
    /** Dollars, when the model's prices are known */
    usd: number | null;
    cards: number;
}

/** A card's share of what a model's answer cost */
export interface MadeBy {
    model: string;
    inputTokens: number | null;
    outputTokens: number | null;
    usd: number | null;
}

export interface Idea {
    id: string;
    projectId: string;
    /** A card of writing, a Think up card that asks a model for cards, or a screenplay (its text is Fountain, see utils/screenplay.ts) */
    kind: 'card' | 'ask' | 'screenplay';
    /** The name to know it by; empty while it has none */
    name: string;
    text: string;
    source: IdeaSource;
    /** Think up cards: what they ask, and what each asking cost */
    ask: IdeaAsk | null;
    /** Screenplays: the title page, the paper, the draft, the versions kept */
    screenplay: ScreenplayMeta | null;
    runs: AskRun[];
    /** Cards a Think up card asked for: which one */
    fromAskId: string | null;
    /** Cards a model wrote: by what, at what cost */
    madeBy: MadeBy | null;
    /** Cards a model wrote: the words they were asked with, to look back on */
    prompt: string | null;
    /** Prompt cards (the generation prompt writer's): which node and generator the prompt was written for */
    promptFor: PromptFor | null;
    /** Where someone put it on the board; null while the page places it */
    at: { x: number; y: number } | null;
    createdBy: string;
    creatorName: string;
    createdAt: number;
    updatedAt: number;
    /** Kept: nobody but whoever kept it (or the owner) changes or deletes it */
    locked: boolean;
    lockedBy: string | null;
    lockedByName: string | null;
    lockedAt: number | null;
    /** Emoji to the ids of the people who reacted with it */
    reactions: Record<string, string[]>;
    /** Who it was given to as a task; null while it is nobody's */
    assigneeId: string | null;
    assigneeName: string | null;
    done: boolean;
    doneAt: number | null;
    doneByName: string | null;
}

export type IdeaNoticeKind = 'task_assigned' | 'task_done';

/** What someone is told in their bell */
export interface IdeaNotice {
    to: string;
    kind: IdeaNoticeKind;
    title: string;
    body: string;
}

export type IdeaOrder = 'newest' | 'loved' | 'oldest';

/** The reactions on offer: approval first */
export const REACTIONS = ['👍', '❤️', '🔥', '😂', '🤔', '👏'] as const;

export const MAX_TEXT = 20_000;
/** A screenplay runs long: a feature is about 180,000 characters of Fountain */
export const MAX_SCREENPLAY_TEXT = 400_000;
export const MAX_NAME = 60;
/** The longest piece of writing a kind of card holds */
export const maxTextOf = (kind: Idea['kind']): number => (kind === 'screenplay' ? MAX_SCREENPLAY_TEXT : MAX_TEXT);
export const MAX_PROMPT = 4000;
export const MAX_RUNS_KEPT = 20;

export const makeId = (): string => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const clean = (text: string, max: number = MAX_TEXT): string => text.replace(/\r\n/g, '\n').replace(/[ \t]+\n/g, '\n').trim().slice(0, max);
const cleanName = (name: string): string => name.replace(/\s+/g, ' ').replace(/^["“'`*#\s]+|["”'`*\s.:]+$/g, '').trim().slice(0, MAX_NAME);

export const firstLine = (text: string): string => (text.split('\n').find(line => line.trim()) ?? '').trim();

/** A card's name as spoken of: its name, else its first line shortened, else a stand-in */
export const titleOf = (idea: Pick<Idea, 'text'> & Partial<Pick<Idea, 'name' | 'kind' | 'ask' | 'screenplay'>>): string => {
    if (idea.name?.trim()) return idea.name.trim();
    if (idea.kind === 'screenplay') return idea.screenplay?.title.trim() || 'Untitled screenplay';
    if (idea.kind === 'ask') return idea.ask?.topic.trim() ? `Think up: ${idea.ask.topic.replace(/\s+/g, ' ').trim().slice(0, 60)}` : 'Think up ideas';
    const line = firstLine(idea.text).replace(/\s+/g, ' ');
    if (!line) return 'An empty card';
    return line.length > 80 ? `${line.slice(0, 79).trimEnd()}…` : line;
};

/** The name tag shown on a card: its name, or its short first line while it has none */
export const tagOf = (idea: Pick<Idea, 'name' | 'text' | 'kind' | 'ask'>): string | null => {
    if (idea.name.trim()) return idea.name.trim();
    if (idea.kind !== 'card') return null;
    const line = firstLine(idea.text).replace(/\s+/g, ' ');
    return line && line.length <= 48 && idea.text.trim().length > line.length ? line : null;
};

export const wordCount = (text: string): number => (text.match(/\S+/g) ?? []).length;

/** A card's text from a piece of writing with a name: the name as its first line */
export const ideaFromText = (text: string, title: string = ''): string => {
    const body = clean(text);
    const name = title.replace(/\s+/g, ' ').trim();
    if (!name) return body;
    return body.startsWith(name) ? body : [name, body].filter(Boolean).join('\n');
};

export interface NewIdeaInput {
    text?: string;
    name?: string;
    source?: IdeaSource;
    kind?: 'card' | 'ask' | 'screenplay';
    ask?: Partial<IdeaAsk>;
    screenplay?: Partial<ScreenplayMeta>;
    fromAskId?: string | null;
    madeBy?: MadeBy | null;
    prompt?: string | null;
    promptFor?: PromptFor | null;
    at?: { x: number; y: number } | null;
}

const PAPERS: Paper[] = ['A4', 'Letter'];
const paperOf = (value: unknown): Paper => (value === 'Letter' ? 'Letter' : 'A4');

/** What a Think up card asks, cleaned; the format fields only when given (the record holds no undefined) */
const askOf = (ask: Partial<IdeaAsk> | undefined): IdeaAsk => ({
    topic: clean(ask?.topic ?? '', MAX_PROMPT),
    buildOnKept: ask?.buildOnKept ?? true,
    ...(ask?.format === 'screenplay' || ask?.format === 'card' || ask?.format === 'prompt' ? { format: ask.format } : {}),
    ...(typeof ask?.pages === 'number' && Number.isFinite(ask.pages) ? { pages: Math.min(20, Math.max(1, Math.round(ask.pages))) } : {}),
    ...(ask?.paper && PAPERS.includes(ask.paper) ? { paper: ask.paper } : {}),
    ...(ask?.target === 'video' || ask?.target === 'image' ? { target: ask.target } : {}),
    ...(typeof ask?.generator === 'string' && ask.generator.trim() ? { generator: ask.generator.trim().slice(0, 80) } : {}),
    ...(typeof ask?.length === 'string' && ask.length.trim() ? { length: ask.length.trim().slice(0, 12) } : {}),
    ...(typeof ask?.frame === 'string' && ask.frame.trim() ? { frame: ask.frame.trim().slice(0, 12) } : {}),
});

/** What a prompt card was written for, as stored; null for any other card or a record that says nothing */
export const promptForOf = (value: unknown): PromptFor | null => {
    const v = value as Record<string, unknown> | null;
    if (!v || typeof v !== 'object' || (v.node !== 'video' && v.node !== 'image')) return null;
    return {
        node: v.node,
        model: typeof v.model === 'string' ? v.model.slice(0, 80) : '',
        ...(typeof v.duration === 'string' && v.duration ? { duration: v.duration.slice(0, 12) } : {}),
        ...(typeof v.aspectRatio === 'string' && v.aspectRatio ? { aspectRatio: v.aspectRatio.slice(0, 12) } : {}),
    };
};

/** A screenplay's title page, paper, draft and versions, cleaned */
const screenplayOf = (meta: Partial<ScreenplayMeta> | undefined | null): ScreenplayMeta => ({
    title: (meta?.title ?? '').replace(/\s+/g, ' ').trim().slice(0, 120),
    author: (meta?.author ?? '').replace(/\s+/g, ' ').trim().slice(0, 120),
    paper: paperOf(meta?.paper),
    draft: typeof meta?.draft === 'number' && meta.draft > 0 ? Math.round(meta.draft) : 1,
    versions: Array.isArray(meta?.versions) ? meta.versions.filter(v => v && typeof v === 'object' && typeof v.text === 'string').map(v => ({
        at: typeof v.at === 'number' ? v.at : 0, by: typeof v.by === 'string' ? v.by : '', byName: typeof v.byName === 'string' ? v.byName : 'Someone',
        note: typeof v.note === 'string' ? v.note.slice(0, 200) : '', text: v.text.slice(0, MAX_SCREENPLAY_TEXT), pages: typeof v.pages === 'number' ? v.pages : 0,
    })) : [],
    ...(Array.isArray(meta?.chat) ? {
        chat: meta.chat.filter(t => t && typeof t === 'object' && typeof t.ask === 'string').slice(-40).map(t => ({
            at: typeof t.at === 'number' ? t.at : 0, ask: t.ask.slice(0, MAX_PROMPT), note: typeof t.note === 'string' ? t.note.slice(0, 300) : '', model: typeof t.model === 'string' ? t.model : 'a model',
            usd: typeof t.usd === 'number' ? t.usd : null, status: t.status === 'kept' || t.status === 'undone' || t.status === 'written' || t.status === 'failed' ? t.status : 'pending',
        })),
    } : {}),
});

export const newIdea = (
    projectId: string,
    creator: { id: string; name: string },
    now: number,
    input: NewIdeaInput = {},
    id: string = makeId(),
): Idea => ({
    id,
    projectId,
    kind: input.kind ?? 'card',
    name: cleanName(input.name ?? ''),
    text: clean(input.text ?? '', maxTextOf(input.kind ?? 'card')),
    source: input.source ?? 'written',
    ask: input.kind === 'ask' ? askOf(input.ask) : null,
    screenplay: input.kind === 'screenplay' ? screenplayOf(input.screenplay) : null,
    runs: [],
    fromAskId: input.fromAskId ?? null,
    madeBy: input.madeBy ?? null,
    prompt: input.prompt ? clean(input.prompt, MAX_PROMPT) : null,
    promptFor: promptForOf(input.promptFor),
    at: input.at ? { x: Math.round(input.at.x), y: Math.round(input.at.y) } : null,
    createdBy: creator.id,
    creatorName: creator.name,
    createdAt: now,
    updatedAt: now,
    locked: false,
    lockedBy: null,
    lockedByName: null,
    lockedAt: null,
    reactions: {},
    assigneeId: null,
    assigneeName: null,
    done: false,
    doneAt: null,
    doneByName: null,
});

const str = (value: unknown): string | null => (typeof value === 'string' ? value : null);
const num = (value: unknown): number | null => (typeof value === 'number' ? value : null);

/** A card as stored, with what an older or partial record lacks filled in */
const madeByOf = (value: unknown): MadeBy | null => {
    const v = value as Record<string, unknown> | null;
    return v && typeof v === 'object' && typeof v.model === 'string'
        ? { model: v.model, inputTokens: num(v.inputTokens), outputTokens: num(v.outputTokens), usd: num(v.usd) }
        : null;
};

export const toIdea = (id: string, projectId: string, data: Record<string, any>): Idea => ({
    id,
    projectId,
    kind: data.kind === 'ask' ? 'ask' : data.kind === 'screenplay' ? 'screenplay' : 'card',
    name: cleanName(str(data.name) ?? ''),
    text: str(data.text) ?? [str(data.title), str(data.text)].filter(Boolean).join('\n'),
    source: data.source === 'ai' || data.source === 'node' ? data.source : 'written',
    ask: data.kind === 'ask'
        ? askOf({ topic: str(data.ask?.topic) ?? '', buildOnKept: data.ask?.buildOnKept !== false, format: data.ask?.format, pages: data.ask?.pages, paper: data.ask?.paper, target: data.ask?.target, generator: data.ask?.generator, length: data.ask?.length, frame: data.ask?.frame })
        : null,
    screenplay: data.kind === 'screenplay' ? screenplayOf(data.screenplay) : null,
    runs: Array.isArray(data.runs) ? data.runs.filter((run: any) => run && typeof run === 'object').map((run: any): AskRun => ({
        at: num(run.at) ?? 0, model: str(run.model) ?? 'a model', inputTokens: num(run.inputTokens), outputTokens: num(run.outputTokens), usd: num(run.usd), cards: num(run.cards) ?? 0,
    })) : [],
    fromAskId: str(data.fromAskId),
    madeBy: madeByOf(data.madeBy),
    prompt: str(data.prompt),
    promptFor: promptForOf(data.promptFor),
    at: num(data.at?.x) !== null && num(data.at?.y) !== null ? { x: data.at.x, y: data.at.y } : null,
    createdBy: str(data.createdBy) ?? '',
    creatorName: str(data.creatorName) ?? 'Someone',
    createdAt: num(data.createdAt) ?? 0,
    updatedAt: num(data.updatedAt) ?? 0,
    locked: data.locked === true,
    lockedBy: str(data.lockedBy),
    lockedByName: str(data.lockedByName),
    lockedAt: num(data.lockedAt),
    reactions: data.reactions && typeof data.reactions === 'object'
        ? Object.fromEntries(Object.entries(data.reactions as Record<string, unknown>)
            .filter(([, who]) => Array.isArray(who))
            .map(([emoji, who]) => [emoji, (who as unknown[]).filter((id): id is string => typeof id === 'string')]))
        : {},
    assigneeId: str(data.assigneeId),
    assigneeName: str(data.assigneeName),
    done: data.done === true,
    doneAt: num(data.doneAt),
    doneByName: str(data.doneByName),
});

// ── Changes to a card ────────────────────────────────────────────────────────

const touched = <T extends Idea>(idea: T, patch: Partial<Idea>, now: number): Idea => ({ ...idea, ...patch, updatedAt: now });

export const setText = (idea: Idea, text: string, now: number): Idea => {
    const next = clean(text, maxTextOf(idea.kind));
    return next === idea.text ? idea : touched(idea, { text: next }, now);
};

/** A screenplay's title page, paper, draft or versions, changed; what is not given stays */
export const setScreenplay = (idea: Idea, meta: Partial<ScreenplayMeta>, now: number): Idea => {
    if (idea.kind !== 'screenplay') return idea;
    const next = screenplayOf({ ...(idea.screenplay ?? {}), ...meta });
    return JSON.stringify(next) === JSON.stringify(idea.screenplay) ? idea : touched(idea, { screenplay: next }, now);
};

/** Put a card where someone dragged it, in whole pixels of the board (anywhere, as a node on the canvas: left of or above where the board began too) */
export const setPlace = (idea: Idea, at: { x: number; y: number } | null, now: number): Idea => {
    const next = at ? { x: Math.round(at.x), y: Math.round(at.y) } : null;
    if ((idea.at === null && next === null) || (idea.at && next && idea.at.x === next.x && idea.at.y === next.y)) return idea;
    return touched(idea, { at: next }, now);
};

export const setName = (idea: Idea, name: string, now: number): Idea => {
    const next = cleanName(name);
    return next === idea.name ? idea : touched(idea, { name: next }, now);
};

/** What a Think up card asks, changed */
export const setAsk = (idea: Idea, ask: Partial<IdeaAsk>, now: number): Idea => {
    if (idea.kind !== 'ask' || !idea.ask) return idea;
    const next: IdeaAsk = askOf({ ...idea.ask, ...ask });
    return JSON.stringify(next) === JSON.stringify(idea.ask) ? idea : touched(idea, { ask: next }, now);
};

/** One more asking of a Think up card, remembered with what it cost; the oldest fall off */
export const addRun = (idea: Idea, run: AskRun): Idea =>
    idea.kind === 'ask' ? touched(idea, { runs: [...idea.runs, run].slice(-MAX_RUNS_KEPT) }, run.at) : idea;

/** What the cards here cost to write, in dollars: each model-written card's share, and what is not known */
export const spentOn = (ideas: ReadonlyArray<Idea>): { usd: number; unknown: number; cards: number } =>
    ideas.filter(idea => idea.kind !== 'ask' && idea.madeBy).reduce(
        (sum, idea) => ({ usd: sum.usd + (idea.madeBy!.usd ?? 0), unknown: sum.unknown + (idea.madeBy!.usd === null ? 1 : 0), cards: sum.cards + 1 }),
        { usd: 0, unknown: 0, cards: 0 },
    );

/** Keep a card (or let it go); who kept it is remembered */
export const setLocked = (idea: Idea, locked: boolean, by: { id: string; name: string }, now: number): Idea =>
    idea.locked === locked ? idea : touched(idea, locked
        ? { locked: true, lockedBy: by.id, lockedByName: by.name, lockedAt: now }
        : { locked: false, lockedBy: null, lockedByName: null, lockedAt: null }, now);

/** Add or take back one person's reaction; an emoji nobody has left any more goes */
export const toggleReaction = (idea: Idea, emoji: string, userId: string, now: number): Idea => {
    if (!(REACTIONS as readonly string[]).includes(emoji)) return idea;
    const had = idea.reactions[emoji] ?? [];
    const who = had.includes(userId) ? had.filter(id => id !== userId) : [...had, userId];
    const reactions = { ...idea.reactions };
    if (who.length === 0) delete reactions[emoji];
    else reactions[emoji] = who;
    return touched(idea, { reactions }, now);
};

export const reactionCount = (idea: Pick<Idea, 'reactions'>): number =>
    Object.values(idea.reactions).reduce((sum, who) => sum + who.length, 0);

/** The reactions on a card in the order they are offered, with how many and whether mine */
export const reactionsOf = (idea: Pick<Idea, 'reactions'>, userId: string | null): { emoji: string; count: number; mine: boolean }[] =>
    REACTIONS.filter(emoji => (idea.reactions[emoji] ?? []).length > 0)
        .map(emoji => ({ emoji, count: idea.reactions[emoji].length, mine: !!userId && idea.reactions[emoji].includes(userId) }));

/** Give a card to someone as a task, or to nobody (null); a card given again is open again */
export const giveIdea = (idea: Idea, person: { id: string; name: string } | null, now: number): Idea => {
    if ((idea.assigneeId ?? null) === (person?.id ?? null)) return idea;
    return touched(idea, { assigneeId: person?.id ?? null, assigneeName: person?.name ?? null, done: false, doneAt: null, doneByName: null }, now);
};

export const setDone = (idea: Idea, done: boolean, by: { name: string }, now: number): Idea =>
    idea.done === done ? idea : touched(idea, { done, doneAt: done ? now : null, doneByName: done ? by.name : null }, now);

// ── Who may do what ──────────────────────────────────────────────────────────

const ownerOf = (people: ReadonlyArray<WorkspacePerson>): string | null => people.find(person => person.permission === 'owner')?.id ?? null;

/** Change the words of a card, give it, keep it: people who may edit the workspace, unless someone else keeps it */
export const canChangeIdea = (idea: Pick<Idea, 'locked' | 'lockedBy'>, userId: string | null, canEditWorkspace: boolean, people: ReadonlyArray<WorkspacePerson>): boolean =>
    !!userId && canEditWorkspace && (!idea.locked || idea.lockedBy === userId || ownerOf(people) === userId);

/** Delete a card: whoever wrote it (while nobody keeps it), and the workspace's owner */
export const canDeleteIdea = (idea: Pick<Idea, 'createdBy' | 'locked'>, userId: string | null, canEditWorkspace: boolean, people: ReadonlyArray<WorkspacePerson>): boolean =>
    !!userId && (ownerOf(people) === userId || (canEditWorkspace && idea.createdBy === userId && !idea.locked));

/** Tick a card done: whoever it was given to, and anyone who may change it */
export const canTickIdea = (idea: Pick<Idea, 'locked' | 'lockedBy' | 'assigneeId'>, userId: string | null, canEditWorkspace: boolean, people: ReadonlyArray<WorkspacePerson>): boolean =>
    !!userId && (idea.assigneeId === userId || canChangeIdea(idea, userId, canEditWorkspace, people));

// ── The board ────────────────────────────────────────────────────────────────

/** The order the page places cards in while nobody has moved any; keeping a card does not move it */
export const sortIdeas = (ideas: ReadonlyArray<Idea>, order: IdeaOrder = 'newest'): Idea[] => [...ideas].sort((a, b) => {
    if (order === 'loved') {
        const byLove = reactionCount(b) - reactionCount(a);
        if (byLove !== 0) return byLove;
    }
    if (order === 'oldest') return a.createdAt - b.createdAt || a.id.localeCompare(b.id);
    return b.createdAt - a.createdAt || a.id.localeCompare(b.id);
});

/**
 * The board's arrangement: the order chosen, with the cards a Think up card asked for right
 * after it (newest asking first), so an asking and its answers stay together.
 */
export const arrangeIdeas = (ideas: ReadonlyArray<Idea>, order: IdeaOrder = 'newest'): Idea[] => {
    const sorted = sortIdeas(ideas, order);
    const asks = new Set(sorted.filter(idea => idea.kind === 'ask').map(idea => idea.id));
    const placed = new Set<string>();
    const out: Idea[] = [];
    for (const idea of sorted) {
        if (placed.has(idea.id)) continue;
        if (idea.fromAskId && asks.has(idea.fromAskId)) continue; // placed with its asking
        out.push(idea);
        placed.add(idea.id);
        if (idea.kind === 'ask') {
            for (const answer of sorted.filter(other => other.fromAskId === idea.id)) { out.push(answer); placed.add(answer.id); }
        }
    }
    return out;
};

// ── What a model wrote ───────────────────────────────────────────────────────

/**
 * A model's answer as one card: its first line is the card's name when it is short and more
 * follows (as the model is asked to write), whatever blank lines or marks it put around it;
 * otherwise the card has no name yet and the whole answer is its text.
 */
export const namedAnswer = (answer: string): { name: string; text: string } => {
    const whole = answer.replace(/\r\n/g, '\n').trim().replace(/^(here is|here's|sure[,!]|okay[,!]|certainly)[^\n]*\n+/i, '');
    const lines = whole.split('\n');
    const at = lines.findIndex(line => line.trim());
    if (at < 0) return { name: '', text: '' };
    const first = cleanName(lines[at].replace(/^\s*(?:name|title)\s*:\s*/i, '').replace(/^\s*#+\s*/, ''));
    const rest = lines.slice(at + 1).join('\n').trim();
    if (rest && first.length > 0 && first.length <= 60 && !/[.!?]$/.test(lines[at].trim()) ) return { name: first, text: rest };
    return { name: '', text: whole };
};

/** Names from a model's answer to "name these", one per line, in the order asked */
export const namesFromAnswer = (answer: string, count: number): string[] => {
    const names = answer.replace(/\r\n/g, '\n').split('\n').map(line => cleanName(line.replace(/^\s*(?:[-*•]|\d+[.)]|\(\d+\))\s*/, ''))).filter(Boolean);
    return names.slice(0, count);
};

// ── Telling people ───────────────────────────────────────────────────────────

/** Someone was given a card as a task: they are told, unless they gave it to themselves */
export const assignNotices = (idea: Pick<Idea, 'text' | 'assigneeId'>, actor: { id: string; name: string }): IdeaNotice[] =>
    idea.assigneeId && idea.assigneeId !== actor.id
        ? [{ to: idea.assigneeId, kind: 'task_assigned', title: `${actor.name} gave you a task`, body: titleOf(idea) }]
        : [];

/** A task was done: whoever wrote the card hears of it, unless they did it */
export const doneNotices = (idea: Pick<Idea, 'text' | 'createdBy' | 'done'>, actor: { id: string; name: string }): IdeaNotice[] =>
    idea.done && idea.createdBy && idea.createdBy !== actor.id
        ? [{ to: idea.createdBy, kind: 'task_done', title: `${actor.name} finished a task`, body: titleOf(idea) }]
        : [];

// ── Words ────────────────────────────────────────────────────────────────────

/** "just now", "5 min ago", "3 h ago", "2 d ago", else the date */
export const agoLabel = (time: number, now: number = Date.now()): string => {
    const seconds = Math.max(0, Math.round((now - time) / 1000));
    if (seconds < 60) return 'just now';
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} h ago`;
    const days = Math.round(hours / 24);
    if (days < 7) return `${days} d ago`;
    return new Date(time).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

export const personName = (people: ReadonlyArray<WorkspacePerson>, id: string | null, fallback: string = 'Someone'): string =>
    (id && people.find(person => person.id === id)?.name) || fallback;

/** A colour for each person of the workspace, by their place in it; the owner gets the first */
export const PERSON_TONES = ['lavender', 'mint', 'lemon', 'blush', 'peach', 'sky'] as const;
export type PersonTone = typeof PERSON_TONES[number];
export const toneOfPerson = (people: ReadonlyArray<WorkspacePerson>, id: string | null): PersonTone => {
    const at = id ? people.findIndex(person => person.id === id) : -1;
    if (at >= 0) return PERSON_TONES[at % PERSON_TONES.length];
    // Someone no longer in the workspace: a steady colour from their id
    const hash = Array.from(id ?? '').reduce((sum, char) => (sum * 31 + char.charCodeAt(0)) >>> 0, 7);
    return PERSON_TONES[hash % PERSON_TONES.length];
};
